'use client';

import * as Dialog from "@radix-ui/react-dialog";
import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSliders,
  faCheck,
  faSpinner,
  faImage,
  faPalette,
  faLocationDot,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";
import {
  faInstagram,
  faFacebook,
  faWhatsapp
} from "@fortawesome/free-brands-svg-icons";
import { useEditorPreferences, EditorPreferences } from "@/hooks/useEditorPreferences";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

interface EditorPreferencesModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: () => void;
}

interface PreferenceOption {
  key: keyof Omit<EditorPreferences, 'skipPopup'>;
  label: string;
  icon: IconDefinition;
  description: string;
}

const PREFERENCE_OPTIONS: PreferenceOption[] = [
  { key: 'logo', label: 'Logo', icon: faImage, description: 'Aplicar logo automaticamente' },
  { key: 'color', label: 'Cor', icon: faPalette, description: 'Aplicar cor primária da marca' },
  { key: 'instagram', label: 'Instagram', icon: faInstagram, description: 'Substituir textos de Instagram' },
  { key: 'facebook', label: 'Facebook', icon: faFacebook, description: 'Substituir textos de Facebook' },
  { key: 'whatsapp', label: 'WhatsApp', icon: faWhatsapp, description: 'Substituir textos de WhatsApp' },
  { key: 'website', label: 'Website', icon: faGlobe, description: 'Substituir textos de website' },
  { key: 'address', label: 'Endereço', icon: faLocationDot, description: 'Substituir textos de endereço' },
];

export function EditorPreferencesModal({
  isOpen,
  onClose,
  onSave
}: EditorPreferencesModalProps) {
  const { preferences, updatePreferences, isSaving } = useEditorPreferences();
  const [localPrefs, setLocalPrefs] = useState<EditorPreferences>(preferences);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLocalPrefs(preferences);
      setDontShowAgain(preferences.skipPopup);
      setIsAnimating(true);
    }
  }, [isOpen, preferences]);

  const handleToggle = (key: keyof EditorPreferences, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (key === 'skipPopup') return;
    setLocalPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    const success = await updatePreferences({
      ...localPrefs,
      skipPopup: dontShowAgain
    });
    if (success) {
      onSave();
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay 
          className="fixed inset-0 z-[1300] bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" 
          onClick={(e) => e.stopPropagation()}
        />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[1400] w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-slate-700/50 bg-slate-900 p-6 shadow-2xl focus:outline-none"
          style={{
            animation: isAnimating ? 'bounceUp 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <style>{`
            @keyframes bounceUp {
              0% { opacity: 0; transform: translate(-50%, -50%) translateY(100px) scale(0.9); }
              50% { transform: translate(-50%, -50%) translateY(-10px) scale(1.02); }
              100% { opacity: 1; transform: translate(-50%, -50%) translateY(0) scale(1); }
            }
          `}</style>

          {/* Header */}
          <div className="text-center mb-6">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-emerald-400">
              <FontAwesomeIcon icon={faSliders} className="h-8 w-8 text-white" />
            </div>
            <Dialog.Title className="text-xl font-bold text-white">
              Defina uma predefinição
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-slate-400">
              Escolha uma configuração para sempre aplicar nas próximas artes
            </Dialog.Description>
          </div>

          {/* Options */}
          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {PREFERENCE_OPTIONS.map(({ key, label, icon, description }) => (
              <button
                key={key}
                type="button"
                onClick={(e) => handleToggle(key, e)}
                className={`flex w-full items-center gap-3 rounded-xl border p-3 transition ${
                  localPrefs[key]
                    ? 'border-purple-500/50 bg-purple-500/10'
                    : 'border-slate-700 bg-slate-800/50 hover:bg-slate-800'
                }`}
              >
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  localPrefs[key] ? 'bg-purple-500/20' : 'bg-slate-700'
                }`}>
                  <FontAwesomeIcon 
                    icon={icon} 
                    className={`h-5 w-5 ${localPrefs[key] ? 'text-purple-400' : 'text-slate-400'}`} 
                  />
                </div>
                <div className="flex-1 text-left">
                  <span className={`block text-sm font-medium ${localPrefs[key] ? 'text-white' : 'text-slate-300'}`}>
                    {label}
                  </span>
                  <span className="text-xs text-slate-500">{description}</span>
                </div>
                <div className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition ${
                  localPrefs[key] 
                    ? 'border-emerald-400 bg-emerald-400' 
                    : 'border-slate-600'
                }`}>
                  {localPrefs[key] && (
                    <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Don't show again checkbox */}
          <label 
            className="mt-4 flex items-center gap-3 cursor-pointer group"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className={`flex h-5 w-5 items-center justify-center rounded border-2 transition ${
                dontShowAgain
                  ? 'border-purple-500 bg-purple-500'
                  : 'border-slate-600 bg-slate-800 group-hover:border-slate-500'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setDontShowAgain(!dontShowAgain);
              }}
            >
              {dontShowAgain && (
                <FontAwesomeIcon icon={faCheck} className="h-3 w-3 text-white" />
              )}
            </div>
            <span 
              className="text-sm text-slate-400 group-hover:text-slate-300 transition"
              onClick={(e) => {
                e.stopPropagation();
                setDontShowAgain(!dontShowAgain);
              }}
            >
              Não exibir da próxima vez
            </span>
          </label>

          {/* Save Button */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleSave();
            }}
            disabled={isSaving}
            className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-emerald-500 px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:shadow-xl hover:brightness-110 disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar preferências'
            )}
          </button>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
